<template>
    <div class="notification-bell">
        <!-- 鈴鐺圖示 -->
        <div class="header-right" :class="hasUnread ? 'active' : ''" @click="openNotificationPanel">
            <span class="icon-bell"></span>
            <!-- 未讀數量徽章 -->
            <span v-if="totalUnreadCount > 0" class="notification-badge">{{ totalUnreadCount > 99 ? '99+' : totalUnreadCount }}</span>
        </div>

        <!-- 遮罩層 -->
        <transition name="fade">
            <div v-if="showNotificationPanel" class="notification-overlay" @click="closeNotificationPanel"></div>
        </transition>

        <!-- 通知面板（從右側滑入） -->
        <transition name="slide">
            <div v-if="showNotificationPanel" class="notification-panel">

                <!-- 面板標題列 -->
                <div class="panel-header">
                    <button class="btn-back" @click="closeNotificationPanel">
                        <i class="fa-solid fa-chevron-left"></i>
                    </button>
                    <div class="panel-title">
                        <i class="fa-solid fa-bell"></i>
                        <span>訊息通知</span>
                        <span v-if="totalUnreadCount > 0" class="unread-badge">{{ totalUnreadCount }}</span>
                    </div>
                </div>

                <!-- 主頁籤切換 -->
                <div class="panel-tabs">
                    <button
                        class="tab-button"
                        :class="{ active: activeTab === 'bulletin' }"
                        @click="switchTab('bulletin')">
                        <span>公佈欄</span>
                        <span v-if="bulletinTotalUnreadCount > 0" class="tab-badge">{{ bulletinTotalUnreadCount }}</span>
                    </button>
                    <button
                        class="tab-button"
                        :class="{ active: activeTab === 'subscription' }"
                        @click="switchTab('subscription')">
                        <span>訂閱作物</span>
                        <span v-if="subscriptionUnreadCount > 0" class="tab-badge">{{ subscriptionUnreadCount }}</span>
                    </button>
                </div>

                <!-- 公佈欄子頁籤 -->
                <div v-if="activeTab === 'bulletin'" class="panel-sub-tabs">
                    <button
                        class="sub-tab-button"
                        :class="{ active: bulletinSubTab === 'latestNews' }"
                        @click="switchBulletinSubTab('latestNews')">
                        <span>最新消息</span>
                        <span v-if="latestNewsUnreadCount > 0" class="sub-tab-badge">{{ latestNewsUnreadCount }}</span>
                    </button>
                    <button
                        class="sub-tab-button"
                        :class="{ active: bulletinSubTab === 'otherNotifications' }"
                        @click="switchBulletinSubTab('otherNotifications')">
                        <span>其他通知</span>
                        <span v-if="bulletinUnreadCount > 0" class="sub-tab-badge">{{ bulletinUnreadCount }}</span>
                    </button>
                </div>

                <!-- 操作按鈕列 -->
                <div class="panel-actions" v-if="showMarkAllButton">
                    <button class="btn-mark-all" @click="markAllAsRead">
                        <i class="fa-regular fa-check-double"></i>
                        全部標記為已讀
                    </button>
                </div>

                <!-- 通知列表 -->
                <div class="panel-content">
                    <!-- 最新消息列表 (公佈欄 > 最新消息) -->
                    <template v-if="activeTab === 'bulletin' && bulletinSubTab === 'latestNews'">
                        <div v-if="latestNewsList.length > 0" class="notification-list">
                            <div
                                v-for="news in latestNewsList"
                                :key="'news-' + news.ID"
                                class="latest-news-item"
                                :class="{ 'is-read': isLatestNewsRead(news.ID) }"
                                @click="handleLatestNewsClick(news)">

                                <!-- 左側圖片 -->
                                <div class="news-image-wrapper">
                                    <img
                                        v-if="getNewsImage(news)"
                                        :src="getNewsImage(news)"
                                        :alt="news.Title"
                                        class="news-image"
                                    />
                                    <div v-else class="news-image-placeholder">
                                        <i class="fa-solid fa-newspaper"></i>
                                    </div>
                                    <!-- 未讀指示點 -->
                                    <span v-if="!isLatestNewsRead(news.ID)" class="news-unread-dot"></span>
                                </div>

                                <!-- 右側內容 -->
                                <div class="news-content">
                                    <div class="news-title">{{ news.Title }}</div>
                                    <div class="news-description">{{ news.Descript }}</div>
                                    <div class="news-footer">
                                        <span class="news-source" v-if="news.Source">{{ news.Source }}</span>
                                        <span class="news-time">{{ formatDateOfRocDate(news.OnTime) }}</span>
                                    </div>
                                </div>

                                <!-- 外部連結圖示 -->
                                <div v-if="news.SourceUrl" class="news-link-icon">
                                    <i class="fa-solid fa-external-link-alt"></i>
                                </div>
                            </div>
                        </div>

                        <!-- 空狀態 -->
                        <div v-else class="empty-state">
                            <div class="empty-icon">
                                <i class="fa-regular fa-newspaper"></i>
                            </div>
                            <p class="empty-title">暫無最新消息</p>
                            <p class="empty-text">目前沒有任何最新消息</p>
                        </div>
                    </template>

                    <!-- 其他通知列表 (公佈欄 > 其他通知) 或 訂閱作物 -->
                    <template v-else>
                        <div v-if="currentTabNotifications.length > 0" class="notification-list">
                            <div
                                v-for="notification in currentTabNotifications"
                                :key="notification.Id"
                                class="notification-item">

                                <!-- 未讀指示器 -->
                                <div class="item-indicator">
                                    <span v-if="notification.IsRead === 0" class="unread-dot"></span>
                                </div>

                                <!-- 通知內容 -->
                                <div class="item-content">
                                    <!-- 第一行：類型標籤 + 標題 -->
                                    <div class="item-header">
                                        <span class="item-badge" :class="getEventTypeClass(notification.Type)">
                                            {{ getEventTypeText(notification.Type) }}
                                        </span>
                                        <span class="item-title">{{ notification.Title }}</span>
                                    </div>

                                    <!-- 第二行：摘要（可點擊展開） -->
                                    <div class="item-summary" @click="toggleNotificationExpand(notification.Id)">
                                        {{ notification.Summary }}
                                        <i
                                            class="expand-icon"
                                            :class="isNotificationExpanded(notification.Id) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'">
                                        </i>
                                    </div>

                                    <!-- 展開的詳細內容 -->
                                    <transition name="expand">
                                        <div v-if="isNotificationExpanded(notification.Id)" class="item-detail">
                                            {{ notification.Content }}
                                        </div>
                                    </transition>

                                    <!-- 第三行：時間與操作 -->
                                    <div class="item-footer">
                                        <span class="item-time">{{ formatDateOfRocDate(notification.Date) }}</span>
                                        <button
                                            v-if="notification.IsRead === 0"
                                            class="btn-mark-read"
                                            @click.stop="markAsRead(notification.Id)">
                                            標記已讀
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 空狀態 -->
                        <div v-else class="empty-state">
                            <div class="empty-icon">
                                <i class="fa-regular fa-bell-slash"></i>
                            </div>
                            <p class="empty-title">暫無通知</p>
                            <p class="empty-text">目前沒有任何通知</p>
                        </div>
                    </template>
                </div>

            </div>
        </transition>
    </div>
</template>

<script>
import { getOrCreateDeviceId } from '@/store/modules/notification'

export default {
    name: 'NotificationBell',
    data() {
        return {
            // 通知列表
            notifications: [],
            // 是否顯示通知面板
            showNotificationPanel: false,
            // 未讀數量
            unreadCount: 0,
            // 輪詢計時器
            pollTimer: null,
            // 輪詢間隔（毫秒）
            pollInterval: 60000, // 每分鐘檢查一次
            // 展開的通知 ID 集合
            expandedNotifications: new Set(),
            // 當前選中的頁籤 ('bulletin' | 'subscription')
            activeTab: 'bulletin',
            // 公佈欄子頁籤 ('latestNews' | 'otherNotifications')
            bulletinSubTab: 'latestNews',
            // 最新消息列表
            latestNewsList: [],
            // 最新消息已讀狀態清單 (NewsID 陣列)
            latestNewsReadStatusList: [],
        };
    },
    computed: {
        hasUnread() {
            return this.totalUnreadCount > 0;
        },
        // 公佈欄通知（非訂閱作物）- 其他通知
        bulletinNotifications() {
            return this.notifications.filter(n => n.Type !== 'CropSubscription');
        },
        // 訂閱作物通知
        subscriptionNotifications() {
            return this.notifications.filter(n => n.Type === 'CropSubscription');
        },
        // 當前選中頁籤的通知（其他通知或訂閱作物）
        currentTabNotifications() {
            if (this.activeTab === 'bulletin' && this.bulletinSubTab === 'otherNotifications') {
                return this.bulletinNotifications;
            }
            return this.subscriptionNotifications;
        },
        // 公佈欄 > 其他通知未讀數量
        bulletinUnreadCount() {
            return this.bulletinNotifications.filter(n => n.IsRead === 0).length;
        },
        // 訂閱作物未讀數量
        subscriptionUnreadCount() {
            return this.subscriptionNotifications.filter(n => n.IsRead === 0).length;
        },
        // 最新消息未讀數量
        latestNewsUnreadCount() {
            return this.latestNewsList.filter(news => !this.isLatestNewsRead(news.ID)).length;
        },
        // 公佈欄總未讀數量（最新消息 + 其他通知）
        bulletinTotalUnreadCount() {
            return this.latestNewsUnreadCount + this.bulletinUnreadCount;
        },
        // 總未讀數量
        totalUnreadCount() {
            return this.bulletinTotalUnreadCount + this.subscriptionUnreadCount;
        },
        // 是否顯示全部標記已讀按鈕
        showMarkAllButton() {
            if (this.activeTab === 'bulletin') {
                if (this.bulletinSubTab === 'latestNews') {
                    return this.latestNewsList.length > 0 && this.latestNewsUnreadCount > 0;
                } else {
                    return this.bulletinNotifications.length > 0 && this.bulletinUnreadCount > 0;
                }
            } else {
                return this.subscriptionNotifications.length > 0 && this.subscriptionUnreadCount > 0;
            }
        }
    },
    mounted() {
        // 初始載入通知
        this.loadNotifications();
        // 載入最新消息
        this.loadLatestNews();

        // 啟動輪詢
        this.startPolling();

        // 監聯 Service Worker 訊息（處理通知點擊）
        this.setupNotificationClickListener();

        // 檢查 URL 參數（處理新視窗開啟的情況）
        this.checkUrlParams();
    },
    beforeDestroy() {
        // 清除輪詢計時器
        this.stopPolling();
    },
    methods: {
        /**
         * 載入通知列表
         */
        loadNotifications() {
            this.FunctionToken(this.getNotifications, {});
        },

        /**
         * 取得通知列表（回調函數）
         */
        async getNotifications(data) {
            // 取得 DeviceId（訪客模式，會等待 Cordova device ready）
            const deviceId = await getOrCreateDeviceId();

            this.userRequest.get('/API/CropSubscription/notification-events', {
                params: {
                    TokenID: data.TokenID,
                    Token: data.Token,
                    DeviceId: deviceId
                }
            })
                .then((res) => {
                    if (res.data) {
                        this.notifications = res.data;
                        this.updateUnreadCount();
                    }
                })
                .catch((err) => {
                    console.error('取得通知列表失敗:', err);
                    if (err.response && err.response.status === 401) {
                        console.warn('登入已過期');
                        // 可選：導向登入頁
                        // window.setPage("login");
                    }
                });
        },

        /**
         * 更新未讀數量
         */
        updateUnreadCount() {
            this.unreadCount = this.notifications.filter(n => n.IsRead === 0).length;
        },

        /**
         * 開啟通知面板
         */
        openNotificationPanel() {
            this.showNotificationPanel = true;
            // 開啟時重新載入
            this.loadNotifications();
            this.loadLatestNews();
            // 防止背景滾動
            document.body.style.overflow = 'hidden';
        },

        /**
         * 關閉通知面板
         */
        closeNotificationPanel() {
            this.showNotificationPanel = false;
            // 恢復背景滾動
            document.body.style.overflow = '';
        },

        /**
         * 標記單筆為已讀
         */
        markAsRead(notificationId) {
            this.FunctionToken((data) => {
                this.updateNotificationIsRead(data, {
                    NotificationEventId: notificationId,
                    MarkAllAsRead: false
                });
            }, {});
        },

        /**
         * 標記全部為已讀（當前頁籤）
         */
        markAllAsRead() {
            if (this.activeTab === 'bulletin' && this.bulletinSubTab === 'latestNews') {
                // 最新消息
                this.markAllLatestNewsAsRead();
            } else {
                // 其他通知或訂閱作物
                const unreadNotifications = this.currentTabNotifications.filter(n => n.IsRead === 0);
                unreadNotifications.forEach(notification => {
                    this.markAsRead(notification.Id);
                });
            }
        },

        /**
         * 更新通知已讀狀態（回調函數）
         */
        async updateNotificationIsRead(authData, updateData) {
            // 取得 DeviceId（訪客模式，會等待 Cordova device ready）
            const deviceId = await getOrCreateDeviceId();

            this.userRequest.post('/API/CropSubscription/notification-events/is-read', {
                TokenID: authData.TokenID,
                Token: authData.Token,
                DeviceId: deviceId,
                ...updateData
            })
                .then((res) => {
                    if (res.data && res.data.Success) {
                        // 重新載入通知列表
                        this.loadNotifications();
                    }
                })
                .catch((err) => {
                    console.error('更新已讀狀態失敗:', err);
                    if (err.response && err.response.status === 401) {
                        console.warn('登入已過期');
                        // 可選：導向登入頁
                        // window.setPage("login");
                    }
                });
        },

        /**
         * 取得事件類型樣式
         */
        getEventTypeClass(eventType) {
            const classMap = {
                'PriceUp': 'badge-up',
                'PriceDown': 'badge-down',
                'PriceAlert': 'badge-alert',
                'CropSubscription': 'badge-subscription'
            };
            return classMap[eventType] || 'badge-default';
        },

        /**
         * 取得事件類型圖示
         */
        getEventTypeIcon(eventType) {
            const iconMap = {
                'PriceUp': 'fa-solid fa-arrow-trend-up',
                'PriceDown': 'fa-solid fa-arrow-trend-down',
                'PriceAlert': 'fa-solid fa-triangle-exclamation',
                'CropSubscription': 'fa-solid fa-chart-line'
            };
            return iconMap[eventType] || 'fa-solid fa-bell';
        },

        /**
         * 取得事件類型文字
         */
        getEventTypeText(eventType) {
            const textMap = {
                'PriceUp': '價格上漲',
                'PriceDown': '價格下跌',
                'PriceAlert': '價格警示',
                'CropSubscription': '作物訂閱'
            };
            return textMap[eventType] || '通知';
        },

        /**
         * 格式化日期
         */
        formatDate(dateString) {
            if (!dateString) return '';

            const date = new Date(dateString);
            const now = new Date();
            const diff = now - date;

            // 一分鐘內
            if (diff < 60000) {
                return '剛剛';
            }
            // 一小時內
            if (diff < 3600000) {
                return Math.floor(diff / 60000) + ' 分鐘前';
            }
            // 一天內
            if (diff < 86400000) {
                return Math.floor(diff / 3600000) + ' 小時前';
            }
            // 一週內
            if (diff < 604800000) {
                return Math.floor(diff / 86400000) + ' 天前';
            }

            // 超過一週，顯示完整日期
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const minute = String(date.getMinutes()).padStart(2, '0');

            return `${year}/${month}/${day} ${hour}:${minute}`;
        },

        formatDateOfRocDate(dateString){
            if (!dateString) return '';

            const date = new Date(dateString);

            const year = date.getFullYear();
            const rocYear = year - 1911;
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${rocYear}-${month}-${day}`;
        },

        /**
         * 啟動輪詢
         */
        startPolling() {
            this.pollTimer = setInterval(() => {
                this.loadNotifications();
                this.loadLatestNews();
            }, this.pollInterval);
        },

        /**
         * 停止輪詢
         */
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },

        /**
         * 切換通知展開/收合狀態
         */
        toggleNotificationExpand(notificationId) {
            if (this.expandedNotifications.has(notificationId)) {
                this.expandedNotifications.delete(notificationId);
            } else {
                this.expandedNotifications.add(notificationId);
            }
            // 觸發響應式更新
            this.$forceUpdate();
        },

        /**
         * 檢查通知是否已展開
         */
        isNotificationExpanded(notificationId) {
            return this.expandedNotifications.has(notificationId);
        },

        /**
         * 切換頁籤
         */
        switchTab(tab) {
            this.activeTab = tab;
        },

        /**
         * 切換公佈欄子頁籤
         */
        switchBulletinSubTab(subTab) {
            this.bulletinSubTab = subTab;
        },

        /**
         * 載入最新消息
         */
        loadLatestNews() {
            this.FunctionToken(this.getLatestNews, {});
        },

        /**
         * 取得最新消息列表（回調函數）
         */
        async getLatestNews(data) {
            try {
                const res = await this.userRequest.post('/API/Default/LatestNewsList', {
                    TokenID: data.TokenID,
                    Token: data.Token
                });

                let resultData = JSON.parse(res.data)

                if (resultData && resultData.menu) {
                    this.latestNewsList = resultData.menu;
                    // 載入已讀狀態
                    this.loadLatestNewsReadStatus();
                }
            } catch (err) {
                console.error('取得最新消息列表失敗:', err);
            }
        },

        /**
         * 載入最新消息已讀狀態
         */
        loadLatestNewsReadStatus() {
            this.FunctionToken(this.getLatestNewsReadStatus, {});
        },

        /**
         * 取得最新消息已讀狀態（回調函數）
         */
        async getLatestNewsReadStatus(data) {
            const deviceId = await getOrCreateDeviceId();

            try {
                const res = await this.userRequest.post('/API/Default/LatestNewsReadStatus', {
                    TokenID: data.TokenID,
                    Token: data.Token,
                    DeviceId: deviceId
                });

                let resultData = JSON.parse(res.data)

                if (resultData && resultData.state && resultData.menu) {
                    // 從 menu 陣列中提取 NewsID
                    this.latestNewsReadStatusList = resultData.menu.map(item => item.NewsID);
                }
            } catch (err) {
                console.error('取得最新消息已讀狀態失敗:', err);
            }
        },

        /**
         * 判斷最新消息是否已讀
         */
        isLatestNewsRead(newsId) {
            return this.latestNewsReadStatusList.includes(parseInt(newsId));
        },

        /**
         * 取得最新消息的圖片URL
         */
        getNewsImage(news) {
            if (news.Files && news.Files.length > 0) {
                // 取得第一個檔案的路徑
                const firstFile = news.Files[0];
                if (firstFile && firstFile.Path) {
                    // 使用與 Home.vue 相同的圖片路徑方式
                    return 'https://agri-plus.yunlin.gov.tw/' + firstFile.Path;
                }
            }
            return null;
        },

        /**
         * 處理最新消息點擊
         */
        async handleLatestNewsClick(news) {
            // 標記為已讀（等待請求送達，避免開連結導致 WebView 切走而中斷這個請求）
            if (!this.isLatestNewsRead(news.ID)) {
                await this.markLatestNewsAsRead(news.ID);
            }

            // 如果有 URL 則另開分頁
            if (news.SourceUrl) {
                window.open(news.SourceUrl, '_blank');
            }
        },

        /**
         * 標記單筆最新消息為已讀
         * 回傳 Promise，並保底加上逾時，避免標記已讀失敗/太慢時卡住導頁
         */
        markLatestNewsAsRead(newsId) {
            return new Promise((resolve) => {
                let settled = false;
                const finish = () => {
                    if (!settled) {
                        settled = true;
                        resolve();
                    }
                };

                setTimeout(finish, 2000);

                this.FunctionToken((data) => {
                    this.updateLatestNewsReadStatus(data, newsId).finally(finish);
                }, {});
            });
        },

        /**
         * 更新最新消息已讀狀態（回調函數）
         */
        async updateLatestNewsReadStatus(authData, newsId) {
            const deviceId = await getOrCreateDeviceId();

            try {
                const res = await this.userRequest.post('/API/Default/LatestNewsReadStatusUpsert', {
                    TokenID: authData.TokenID,
                    Token: authData.Token,
                    DeviceId: deviceId,
                    NewsID: parseInt(newsId)
                });

                let resultData = JSON.parse(res.data)

                if (resultData && resultData.state) {
                    // 將此 newsId 加入已讀清單
                    if (!this.latestNewsReadStatusList.includes(parseInt(newsId))) {
                        this.latestNewsReadStatusList.push(parseInt(newsId));
                    }
                }
            } catch (err) {
                console.error('更新最新消息已讀狀態失敗:', err);
            }
        },

        /**
         * 標記全部最新消息為已讀
         */
        markAllLatestNewsAsRead() {
            const unreadNews = this.latestNewsList.filter(news => !this.isLatestNewsRead(news.ID));
            unreadNews.forEach(news => {
                this.markLatestNewsAsRead(news.ID);
            });
        },

        /**
         * 設定通知點擊監聽器（監聽 Service Worker 訊息）
         */
        setupNotificationClickListener() {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.addEventListener('message', (event) => {
                    if (event.data && event.data.type === 'OPEN_NOTIFICATION_PANEL') {
                        console.log('[NotificationBell] 收到開啟通知面板訊息:', event.data);

                        // 開啟通知面板
                        this.openNotificationPanel();

                        // 根據通知類型切換到對應頁籤
                        if (event.data.data && event.data.data.notificationType) {
                            const type = event.data.data.notificationType;
                            if (type === 'CropSubscription') {
                                this.switchTab('subscription');
                            } else {
                                this.switchTab('bulletin');
                            }
                        }
                    }
                });

                console.log('[NotificationBell] Service Worker 訊息監聽器已設置');
            } else {
                console.log('[NotificationBell] 此環境不支援 Service Worker');
            }
        },

        /**
         * 檢查 URL 參數（處理新視窗開啟的情況）
         */
        checkUrlParams() {
            const urlParams = new URLSearchParams(window.location.search);

            if (urlParams.get('openNotifications') === 'true') {
                console.log('[NotificationBell] 檢測到開啟通知參數');

                // 延遲一下確保元件已完全載入
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.openNotificationPanel();

                        // 根據通知類型切換到對應頁籤
                        const notificationType = urlParams.get('notificationType');
                        if (notificationType === 'CropSubscription') {
                            this.switchTab('subscription');
                        } else {
                            this.switchTab('bulletin');
                        }
                    }, 300);
                });

                // 清除 URL 參數（保持 URL 乾淨）
                const cleanUrl = window.location.pathname + window.location.hash;
                window.history.replaceState({}, document.title, cleanUrl);
            }
        }
    }
};
</script>

<style scoped>
/* ==================== 鈴鐺圖示 ==================== */
.header-right {
    position: relative;
    cursor: pointer;
    padding: 8px;
    transition: all 0.2s ease;
}

.notification-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    background-color: #F16767;
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1.2;
    min-width: 18px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* ==================== 遮罩層 ==================== */
.notification-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 9998;
    -webkit-tap-highlight-color: transparent;
}

/* 遮罩淡入淡出動畫 */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}

/* ==================== 通知面板 ==================== */
.notification-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 400px;
    background-color: #F9F9F9;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
}

/* 面板滑入滑出動畫 */
.slide-enter-active, .slide-leave-active {
    transition: transform 0.25s ease-out;
}

.slide-enter, .slide-leave-to {
    transform: translateX(100%);
}

/* 手機版：面板寬度100% */
@media (max-width: 767px) {
    .notification-panel {
        max-width: 100%;
    }
}

/* ==================== 面板標題列 ==================== */
.panel-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background-color: #19547b;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-back {
    background: transparent;
    border: none;
    color: white;
    font-size: 22px;
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    -webkit-tap-highlight-color: transparent;
    margin-left: -4px;
}

.btn-back:hover {
    transform: translateX(-2px);
}

.btn-back:active {
    transform: scale(0.95);
}

.panel-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
    font-weight: bold;
    flex: 1;
}

.panel-title i {
    font-size: 20px;
}

.unread-badge {
    background-color: rgba(255, 255, 255, 0.25);
    color: white;
    border-radius: 10px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 600;
}

/* ==================== 頁籤切換 ==================== */
.panel-tabs {
    display: flex;
    background-color: white;
    border-bottom: 2px solid #e0e0e0;
}

.tab-button {
    flex: 1;
    padding: 12px 16px;
    background-color: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    color: #666;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    -webkit-tap-highlight-color: transparent;
}

.tab-button:hover {
    background-color: #f8f9fa;
}

.tab-button.active {
    color: #19547b;
    border-bottom-color: #19547b;
    font-weight: 600;
}

.tab-badge {
    background-color: #F16767;
    color: white;
    border-radius: 10px;
    padding: 1px 6px;
    font-size: 11px;
    font-weight: 600;
    min-width: 18px;
    text-align: center;
}

.tab-button.active .tab-badge {
    background-color: #19547b;
}

/* ==================== 公佈欄子頁籤（小按鈕樣式） ==================== */
.panel-sub-tabs {
    display: flex;
    gap: 8px;
    padding: 10px 16px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
}

.sub-tab-button {
    padding: 6px 14px;
    background-color: white;
    border: 1px solid #d0d0d0;
    border-radius: 20px;
    color: #666;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    -webkit-tap-highlight-color: transparent;
}

.sub-tab-button:hover {
    background-color: #f0f0f0;
    border-color: #bbb;
}

.sub-tab-button.active {
    color: white;
    background-color: #19547b;
    border-color: #19547b;
    font-weight: 600;
}

.sub-tab-badge {
    background-color: #F16767;
    color: white;
    border-radius: 10px;
    padding: 1px 6px;
    font-size: 10px;
    font-weight: 600;
    min-width: 16px;
    text-align: center;
}

.sub-tab-button.active .sub-tab-badge {
    background-color: rgba(255, 255, 255, 0.9);
    color: #19547b;
}

/* ==================== 操作按鈕列 ==================== */
.panel-actions {
    padding: 12px 16px;
    background-color: white;
    border-bottom: 1px solid #e0e0e0;
}

.btn-mark-all {
    width: 100%;
    padding: 10px 16px;
    background-color: #19547b;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    -webkit-tap-highlight-color: transparent;
}

.btn-mark-all:hover {
    background-color: #036EB8;
}

.btn-mark-all:active {
    transform: scale(0.98);
    background-color: #14405e;
}

/* ==================== 面板內容 ==================== */
.panel-content {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 20px;
}

/* 自訂滾動條 */
.panel-content::-webkit-scrollbar {
    width: 6px;
}

.panel-content::-webkit-scrollbar-track {
    background: transparent;
}

.panel-content::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* ==================== 通知列表 ==================== */
.notification-list {
    background-color: white;
}

/* ==================== 通知項目 ==================== */
.notification-item {
    display: flex;
    padding: 14px 16px;
    border-bottom: 1px solid #e8e8e8;
    -webkit-tap-highlight-color: transparent;
}

/* 未讀指示器 */
.item-indicator {
    width: 12px;
    padding-top: 2px;
    flex-shrink: 0;
}

.unread-dot {
    display: block;
    width: 6px;
    height: 6px;
    background-color: #19547b;
    border-radius: 50%;
}

/* 通知內容 */
.item-content {
    flex: 1;
    min-width: 0;
}

/* 第一行：類型 + 標題 */
.item-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.item-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 500;
    color: white;
    flex-shrink: 0;
}

.badge-up {
    background-color: #0cb802;
}

.badge-down {
    background-color: #F16767;
}

.badge-alert {
    background-color: #EBB63C;
    color: #343a40;
}

.badge-subscription {
    background-color: #19547b;
}

.badge-default {
    background-color: #6c757d;
}

.item-title {
    font-size: 15px;
    font-weight: 500;
    color: #333;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* 第二行：摘要（可點擊展開） */
.item-summary {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 6px;
    cursor: pointer;
    padding: 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    position: relative;
    padding-right: 30px;
    transition: background-color 0.2s ease;
    -webkit-tap-highlight-color: transparent;
}

.item-summary:hover {
    background-color: #e9ecef;
}

.expand-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: #19547b;
    transition: transform 0.2s ease;
}

/* 展開的詳細內容 */
.item-detail {
    font-size: 13px;
    color: #555;
    line-height: 1.6;
    margin-bottom: 8px;
    padding: 10px;
    background-color: #fff;
    border-left: 3px solid #19547b;
    border-radius: 4px;
    white-space: pre-line;
    max-height: 300px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

/* 詳細內容滾動條 */
.item-detail::-webkit-scrollbar {
    width: 4px;
}

.item-detail::-webkit-scrollbar-track {
    background: transparent;
}

.item-detail::-webkit-scrollbar-thumb {
    background: rgba(25, 84, 123, 0.3);
    border-radius: 2px;
}

.item-detail::-webkit-scrollbar-thumb:hover {
    background: rgba(25, 84, 123, 0.5);
}

/* 展開動畫 */
.expand-enter-active, .expand-leave-active {
    transition: all 0.3s ease;
    max-height: 1000px;
    overflow: hidden;
}

.expand-enter, .expand-leave-to {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 0;
}

/* 第三行：時間與操作 */
.item-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.item-time {
    font-size: 12px;
    color: #999;
}

/* 標記已讀按鈕 */
.btn-mark-read {
    background: transparent;
    border: 1px solid #19547b;
    color: #19547b;
    padding: 3px 10px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    -webkit-tap-highlight-color: transparent;
}

.btn-mark-read:hover {
    background-color: #f0f8ff;
}

.btn-mark-read:active {
    background-color: #19547b;
    color: white;
}

/* ==================== 最新消息項目 ==================== */
.latest-news-item {
    display: flex;
    align-items: flex-start;
    padding: 14px 16px;
    border-bottom: 1px solid #e8e8e8;
    cursor: pointer;
    transition: background-color 0.2s ease;
    -webkit-tap-highlight-color: transparent;
}

.latest-news-item:hover {
    background-color: #f8f9fa;
}

.latest-news-item.is-read {
    background-color: #fafafa;
}

.latest-news-item.is-read .news-title,
.latest-news-item.is-read .news-description {
    color: #999;
}

/* 左側圖片區塊 */
.news-image-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
    flex-shrink: 0;
    margin-right: 12px;
}

.news-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
    background-color: #f0f0f0;
}

.news-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e9ecef;
    border-radius: 6px;
    color: #adb5bd;
    font-size: 24px;
}

.news-unread-dot {
    position: absolute;
    top: -3px;
    left: -3px;
    width: 10px;
    height: 10px;
    background-color: #19547b;
    border-radius: 50%;
    border: 2px solid white;
}

/* 右側內容區塊 */
.news-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.news-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.news-description {
    font-size: 13px;
    color: #666;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.news-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
}

.news-source {
    font-size: 11px;
    color: #19547b;
    background-color: rgba(25, 84, 123, 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    max-width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.news-time {
    font-size: 11px;
    color: #999;
}

/* 外部連結圖示 */
.news-link-icon {
    flex-shrink: 0;
    margin-left: 8px;
    color: #19547b;
    font-size: 14px;
    opacity: 0.6;
    transition: opacity 0.2s ease;
}

.latest-news-item:hover .news-link-icon {
    opacity: 1;
}

/* ==================== 空狀態 ==================== */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    text-align: center;
}

.empty-icon {
    font-size: 64px;
    color: #d0d0d0;
    margin-bottom: 20px;
}

.empty-title {
    font-size: 18px;
    font-weight: bold;
    color: #666;
    margin: 0 0 8px 0;
}

.empty-text {
    font-size: 14px;
    color: #999;
    margin: 0;
}

/* ==================== 響應式調整 ==================== */
@media (max-width: 576px) {
    .panel-header {
        padding: 14px 16px;
        gap: 10px;
    }

    .btn-back {
        font-size: 20px;
    }

    .panel-title {
        font-size: 16px;
    }

    .panel-tabs {
        border-bottom: 1px solid #e0e0e0;
    }

    .tab-button {
        padding: 10px 12px;
        font-size: 14px;
        border-bottom-width: 2px;
    }

    .tab-badge {
        font-size: 10px;
        padding: 1px 5px;
        min-width: 16px;
    }

    .panel-actions {
        padding: 10px 12px;
    }

    .notification-item {
        padding: 12px 14px;
    }

    .item-title {
        font-size: 14px;
    }

    .item-summary {
        font-size: 13px;
        padding: 6px;
        padding-right: 26px;
    }

    .item-detail {
        font-size: 12px;
        padding: 8px;
        max-height: 250px;
    }

    .expand-icon {
        font-size: 11px;
        right: 8px;
    }

    .item-badge {
        font-size: 9px;
        padding: 2px 5px;
    }

    .btn-mark-read {
        font-size: 10px;
        padding: 2px 8px;
    }

    /* 子頁籤響應式 */
    .panel-sub-tabs {
        padding: 8px 12px;
        gap: 6px;
    }

    .sub-tab-button {
        padding: 5px 12px;
        font-size: 12px;
    }

    .sub-tab-badge {
        font-size: 9px;
        padding: 1px 5px;
        min-width: 14px;
    }

    /* 最新消息響應式 */
    .latest-news-item {
        padding: 12px 14px;
    }

    .news-image-wrapper {
        width: 60px;
        height: 60px;
        margin-right: 10px;
    }

    .news-image-placeholder {
        font-size: 20px;
    }

    .news-unread-dot {
        width: 8px;
        height: 8px;
    }

    .news-title {
        font-size: 14px;
    }

    .news-description {
        font-size: 12px;
    }

    .news-source {
        font-size: 10px;
        max-width: 80px;
    }

    .news-time {
        font-size: 10px;
    }

    .news-link-icon {
        font-size: 12px;
    }
}
</style>
